// =============================================================================
// Lairo Status — shared React components & helpers.
// =============================================================================
import { useState, useEffect } from "react";
import { COMPONENT_STATUS } from "../model";
import logoLight from "../assets/Lairo-Logo-Large.png";
import logoDark from "../assets/Lairo-Logo-dark.png";

/* ---- time helpers ------------------------------------------------------ */
export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function fmtTime(t) {
  const d = new Date(t);
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")} ${ap}`;
}
export function fmtDate(t, opts = {}) {
  const d = new Date(t);
  const base = `${MONTHS[d.getMonth()]} ${d.getDate()}`;
  return opts.year ? `${base}, ${d.getFullYear()}` : base;
}
export function fmtDateTime(t) {
  return `${fmtDate(t)} · ${fmtTime(t)}`;
}
export function relTime(t) {
  const diff = Date.now() - t;
  const min = Math.round(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr${hr > 1 ? "s" : ""} ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day} day${day > 1 ? "s" : ""} ago`;
  return fmtDate(t, { year: true });
}

/* ---- tone → color ------------------------------------------------------ */
const TONE_VAR = { op: "--st-op", deg: "--st-deg", part: "--st-part", maj: "--st-maj", mnt: "--st-mnt", info: "--accent" };
const TONE_BG = { op: "--st-op-bg", deg: "--st-deg-bg", part: "--st-part-bg", maj: "--st-maj-bg", mnt: "--st-mnt-bg" };
export function toneColor(tone) {
  return `var(${TONE_VAR[tone] || "--text-muted"})`;
}
export function toneBg(tone) {
  return `var(${TONE_BG[tone] || "--surface-2"})`;
}

/* ---- icons (minimal stroke) ------------------------------------------- */
export function Icon({ name, size = 16, stroke = 2 }) {
  const p = {
    width: size, height: size, viewBox: "0 0 24 24", fill: "none",
    stroke: "currentColor", strokeWidth: stroke, strokeLinecap: "round", strokeLinejoin: "round",
  };
  const paths = {
    check: <path d="M20 6 9 17l-5-5" />,
    alert: <><path d="M12 8v5" /><path d="M12 17h.01" /><circle cx="12" cy="12" r="9" /></>,
    warn: <><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></>,
    wrench: <path d="M14.7 6.3a4 4 0 0 0-5.2 5.2L3 18l3 3 6.5-6.5a4 4 0 0 0 5.2-5.2l-2.4 2.4-2.6-.7-.7-2.6 2.4-2.4Z" />,
    minus: <path d="M5 12h14" />,
    x: <><path d="M18 6 6 18" /><path d="m6 6 12 12" /></>,
    bell: <><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.9 1.9 0 0 0 3.4 0" /></>,
    sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></>,
    moon: <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />,
    chevron: <path d="m6 9 6 6 6-6" />,
    plus: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    rss: <><path d="M4 11a9 9 0 0 1 9 9" /><path d="M4 4a16 16 0 0 1 16 16" /><circle cx="5" cy="19" r="1" /></>,
    arrow: <><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></>,
    edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
    trash: <><path d="M3 6h18" /><path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /></>,
    dot: <circle cx="12" cy="12" r="6" fill="currentColor" stroke="none" />,
    logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" /></>,
  };
  return <svg {...p} style={{ display: "block", flexShrink: 0 }}>{paths[name] || null}</svg>;
}

export const STATUS_ICON = { op: "check", deg: "warn", part: "alert", maj: "x", mnt: "wrench" };

/* ---- status dot -------------------------------------------------------- */
export function StatusDot({ tone, size = 10, pulse = false }) {
  return (
    <span style={{ position: "relative", display: "inline-flex", width: size, height: size, flexShrink: 0 }}>
      {pulse && <span style={{ position: "absolute", inset: 0, borderRadius: "50%", background: toneColor(tone), opacity: 0.5, animation: "lairoPulse 2s ease-out infinite" }} />}
      <span style={{ width: size, height: size, borderRadius: "50%", background: toneColor(tone) }} />
    </span>
  );
}

/* ---- badge / pill ------------------------------------------------------ */
export function Badge({ tone, children, solid = false, icon }) {
  const style = solid
    ? { background: toneColor(tone), color: "var(--accent-contrast)" }
    : { background: toneBg(tone), color: toneColor(tone) };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px",
      borderRadius: 999, fontSize: 12, fontWeight: 600, lineHeight: 1.4, whiteSpace: "nowrap", ...style }}>
      {icon && <Icon name={icon} size={12} stroke={2.5} />}
      {children}
    </span>
  );
}

/* ---- 90-day uptime bars ------------------------------------------------ */
export function UptimeBars({ history }) {
  const [hover, setHover] = useState(null);
  const days = history || [];
  return (
    <div style={{ position: "relative" }}>
      <div style={{ display: "flex", gap: 2, height: 34, alignItems: "stretch" }} onMouseLeave={() => setHover(null)}>
        {days.map((d, i) => {
          const tone = d.status === "operational" ? "op"
            : d.status === "degraded" ? "deg"
            : d.status === "partial" ? "part"
            : d.status === "maintenance" ? "mnt" : "maj";
          return (
            <div key={i} onMouseEnter={() => setHover(i)}
              style={{ flex: 1, minWidth: 2, borderRadius: 2, background: toneColor(tone),
                opacity: hover === null || hover === i ? 1 : 0.55, transition: "opacity .12s", cursor: "default" }} />
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 7, fontSize: 11.5, color: "var(--text-faint)" }}>
        <span>90 days ago</span>
        <span>Today</span>
      </div>
      {hover !== null && days[hover] && (
        <Tooltip index={hover} total={days.length}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>{fmtDate(days[hover].date, { year: true })}</div>
          <div style={{ color: "var(--text-muted)" }}>
            {days[hover].status === "operational"
              ? "No downtime recorded"
              : `${COMPONENT_STATUS[days[hover].status]?.label} · ${days[hover].pct.toFixed(1)}%`}
          </div>
        </Tooltip>
      )}
    </div>
  );
}

export function Tooltip({ index, total, children }) {
  const left = `${((index + 0.5) / total) * 100}%`;
  return (
    <div style={{ position: "absolute", bottom: "calc(100% + 8px)", left, transform: "translateX(-50%)",
      background: "var(--text)", color: "var(--bg)", padding: "7px 10px", borderRadius: 8, fontSize: 12,
      whiteSpace: "nowrap", boxShadow: "var(--shadow)", pointerEvents: "none", zIndex: 20 }}>
      {children}
      <span style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)",
        borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: "5px solid var(--text)" }} />
    </div>
  );
}

/* ---- theme toggle ------------------------------------------------------ */
export function useTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem("lairo_theme") || "light");
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("lairo_theme", theme);
  }, [theme]);
  return [theme, setTheme];
}
export function ThemeToggle({ theme, setTheme }) {
  return (
    <button className="icon-btn" aria-label="Toggle theme" onClick={() => setTheme(theme === "light" ? "dark" : "light")}>
      <Icon name={theme === "light" ? "moon" : "sun"} size={17} />
    </button>
  );
}

/* ---- small layout helpers --------------------------------------------- */
export function Card({ children, style, ...rest }) {
  return <div className="card" style={style} {...rest}>{children}</div>;
}

/* ---- brand logo (swaps per theme) ------------------------------------- */
export function Logo({ height = 22 }) {
  const w = height * (877 / 178);
  const common = { height, width: w, verticalAlign: "middle" };
  return (
    <span style={{ display: "inline-flex", lineHeight: 0 }}>
      <img className="logo-light" src={logoLight} alt="Lairo" style={common} />
      <img className="logo-dark" src={logoDark} alt="Lairo" style={common} />
    </span>
  );
}

export function fmtDuration(start, end) {
  if (!start || !end) return "";
  const diff = end - start;
  if (diff <= 0) return "";
  const min = Math.round(diff / 60000);
  if (min < 60) return `${min} minute${min > 1 ? "s" : ""}`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  if (remMin === 0) return `${hr} hour${hr > 1 ? "s" : ""}`;
  return `${hr} hour${hr > 1 ? "s" : ""} ${remMin} minute${remMin > 1 ? "s" : ""}`;
}
